/**
 * Viewport hook + breakpoints.
 *
 * Three modes per the app-shell spec:
 *   - mobile  (< 768): bottom-tab + FAB chrome
 *   - tablet  (>= 768, < 1280): sidebar + main, no right rail
 *   - wide    (>= 1280): sidebar + main + right rail
 *
 * Sharp edges:
 *   - SSR / non-browser callers: returns 'mobile' when `window` is undefined,
 *     so the SPA never crashes during prerender / Node test bootstrap.
 *   - Listener cleans up on unmount; we don't share a single window listener
 *     across instances because React strict-mode double-mount makes that
 *     harder to reason about than a per-instance listener with a tiny
 *     overhead.
 */
import { useEffect, useState } from 'react';

export type Viewport = 'mobile' | 'tablet' | 'wide';

export const VIEWPORT_BREAKPOINTS = {
  tablet: 768,
  wide: 1280,
} as const;

export function getViewport(width: number): Viewport {
  if (width < VIEWPORT_BREAKPOINTS.tablet) return 'mobile';
  if (width < VIEWPORT_BREAKPOINTS.wide) return 'tablet';
  return 'wide';
}

const readInitialViewport = (): Viewport => {
  if (typeof window === 'undefined') return 'mobile';
  return getViewport(window.innerWidth);
};

export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(readInitialViewport);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onResize = (): void => {
      setViewport(getViewport(window.innerWidth));
    };

    // Re-read in case the layout settled to a different width before mount.
    onResize();

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return viewport;
}
